/**
 * BoardV3 Task Box Overlay
 * Shown during drag operations.
 * Reads detailLevel from boardLayoutStore.
 */

import { memo } from 'react';
import type { Task, Employee } from '../../../types';
import { BoardV3TaskBoxContent } from './BoardV3TaskBoxContent';
import { PADDINGS, BORDER_RADII } from '../../hooks/useBoardTaskDetailLevel';
import { useDayWidth, useRowHeight, useDetailLevel } from '../BoardLayoutContext';
import { useEmployees, useScheduledTasks } from '../../../stores/hooks';

interface BoardV3TaskBoxOverlayProps {
  task: Task;
  bulkDragCount?: number;
}

export const BoardV3TaskBoxOverlay = memo(function BoardV3TaskBoxOverlay({
  task,
  bulkDragCount = 0,
}: BoardV3TaskBoxOverlayProps) {
  const employees = useEmployees();
  const scheduledTasks = useScheduledTasks();
  const dayWidth = useDayWidth();
  const rowHeight = useRowHeight();
  const detailLevel = useDetailLevel();

  const duration = scheduledTasks.get(task.id)?.effectiveDays ?? 10;
  const padding = PADDINGS[detailLevel];
  const width = Math.max(duration * dayWidth - padding * 2, 40);
  const height = rowHeight - padding * 2;

  const assignees = task.assigneeIds
    .map((id) => employees.find((e) => e.id === id))
    .filter((e): e is Employee => e !== undefined);

  const scheduled = scheduledTasks.get(task.id);

  return (
    <div
      style={{
        width,
        height,
        cursor: 'grabbing',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        borderRadius: BORDER_RADII[detailLevel],
        position: 'relative',
      }}
    >
      <BoardV3TaskBoxContent
        task={task}
        assignees={assignees}
        detailLevel={detailLevel}
        scheduled={scheduled}
        cardWidth={width}
      />
      {bulkDragCount > 1 && (
        <div
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            minWidth: 20,
            height: 20,
            borderRadius: 10,
            backgroundColor: '#3b82f6',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 5px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          {bulkDragCount}
        </div>
      )}
    </div>
  );
});
